import { useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../../components/ui/hover-card";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Loader2 } from "lucide-react";
import { useSmartFilter } from "./SmartFilter";

const PENDING_CLS = "border-amber-300 bg-amber-50 text-amber-800";
export const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Not Login Yet");
const BASE_FILTERS = [
  { key: "status", label: "Status", get: (a) => a.status },
  { key: "entity", label: "Entity", get: (a) => (a.entity_type && a.entity_type !== "—" ? a.entity_type : "") },
  { key: "state", label: "State", get: (a) => a.state },
  { key: "city", label: "City", get: (a) => a.city },
  { key: "login", label: "Login", get: (a) => (a.last_login ? "Has logged in" : "Never logged in") },
];
const INTRO_FILTERS = [{ key: "intro", label: "Introduced by", get: (a) => a.introduced_by }, { key: "profession", label: "Profession", get: (a) => a.profession }, ...BASE_FILTERS];

// EUIN is personal: shown only for Individuals; for corporates/firms it lives on their employees.
const indiv = (t) => (a) => String(a[t] || "").toLowerCase() === "individual";
const TICK = [["BSE Login ID", "bse_login_code"], ["Old Code", "old_code", (a) => !!a.old_code], ["Sub Broker ARN Code", "arn"], ["EUIN", "euin", indiv("entity_type")]];
const INTRO_TICK = [["BSE Login ID", "bse_login_code"], ["Old Code", "old_code", (a) => !!a.old_code], ["Entity Type", "intro_entity"], ["ARN Code", "main_arn"],
  ["EUIN", "main_euin"], ["EUIN Holder", "intro_euin_holder"], ["City, State", "intro_city"], ["Contact", "intro_contact"]];
// Shows the code; hovering reveals BSE login code + ARN/EUIN (the main ARN holder's for introducers).
export const CodeTicker = ({ a, intro, ticks, codeKey = "code", note }) => (
  <HoverCard openDelay={120} closeDelay={60}>
    <HoverCardTrigger asChild>
      <span className="cursor-help border-b border-dotted border-slate-400 font-medium" data-testid={`code-cell-${a.id}`}>{a[codeKey] || "—"}</span>
    </HoverCardTrigger>
    <HoverCardContent className={intro || ticks ? "w-80 p-3" : "w-64 p-3"} data-testid={`code-tooltip-${a.id}`}>
      {note && <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-teal-700">{note}</p>}
      {(ticks || (intro ? INTRO_TICK : TICK)).filter(([, , show]) => !show || show(a)).map(([lbl, k]) => (
        <div key={k} className="flex justify-between gap-3 py-0.5 text-xs">
          <span className="shrink-0 text-slate-500">{lbl}</span><span className="text-right font-mono text-slate-900 [overflow-wrap:anywhere]" data-testid={`code-${k}-${a.id}`}>{a[k] || "—"}</span>
        </div>))}
    </HoverCardContent>
  </HoverCard>
);

// One searchable, filterable associate table (used for the Associate and Introducer tabs).
export const AssocList = ({ rows, loading, kind, statusCls, Menu }) => {
  const [showInactive, setShowInactive] = useState(false);
  const intro = kind === "introducer";
  const sf = useSmartFilter(rows, { text: ["name", "code", "old_code", "arn", "euin", "city", "state", "entity_type", "status", "introduced_by"], filters: intro ? INTRO_FILTERS : BASE_FILTERS,
    placeholder: "Type name, code, ARN, EUIN, city…", testid: kind, initialSort: "code", hidden: (a, sel) => a.status === "Inactive" && !showInactive && sel.status !== "Inactive" });
  const shown = sf.filtered;
  const { Th } = sf;
  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-4">
        {sf.ui}
        <label className="flex cursor-pointer select-none items-center gap-2 text-xs text-gray-600">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="rounded accent-teal-600" data-testid={`${kind}-show-inactive`} />
          Show inactive ({rows.filter((a) => a.status === "Inactive").length})
        </label>
        <span className="ml-auto text-xs text-gray-500" data-testid={`${kind}-count`}>{shown.length} of {sf.total}</span>
      </div>
      {loading ? <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${kind}-table`}>
            <thead><tr className="border-b text-left text-xs uppercase text-slate-500">
              <Th k="name" className="py-2 pr-3">{intro ? "Introducer Name" : "Associate Name"}</Th><Th k="code">Sub Broker Code</Th>
              {intro && <Th k="introduced_by">Introduced By</Th>}<Th k="city">City, State</Th>
              <Th k="entity_type">Entity Type</Th><Th k="last_login">Last Login</Th><Th k="status">Status</Th><th>Action</th></tr></thead>
            <tbody>{shown.map((a) => (
              <tr key={a.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`associate-row-${a.id}`}>
                <td className="py-2 pr-3 font-medium">{a.name}</td><td className="pr-3"><CodeTicker a={a} intro={intro} /></td>
                {intro && <td className="pr-3 text-xs">{a.introduced_by || "—"}</td>}
                <td className="pr-3">{[a.city, a.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="pr-3">{a.entity_type}</td><td className="pr-3 text-xs">{fmt(a.last_login)}</td>
                <td className="pr-3"><Badge variant="outline" className={statusCls[a.status] || PENDING_CLS}>{a.status}</Badge></td>
                <td><Menu a={a} /></td>
              </tr>))}
              {!shown.length && <tr><td colSpan={8} className="py-8 text-center text-slate-500">Nothing matches</td></tr>}
            </tbody>
          </table>
        </div>)}
    </Card>
  );
};
